// src/components/Chat/ChatComponent.js

import React, { useEffect, useRef, useState } from "react";
import { useSocket } from "../../context/SocketContext";
import { getChatHistory } from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import "./ChatComponent.css";
import { Send } from "@mui/icons-material";

const ChatComponent = ({ userId, userType, title = "Chat" }) => {
  const socket = useSocket();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const typingTimeout = useRef(null);
  const messagesEndRef = useRef(null);
  const id = JSON.parse(localStorage.getItem("Data"))?.id;

  /*
    Backend room:
    room_{chatId}

    chatId = user's ID

    Example:
    room_customer-123
  */

  const chatId = userId;

  // ===============================
  // SOCKET STATUS
  // ===============================

  useEffect(() => {
    if (!socket) return;

    const connect = () => {
      console.log("✅ Chat Connected");

      setIsSocketConnected(true);
    };

    const disconnect = (reason) => {
      console.log("❌ Socket Disconnect", reason);

      setIsSocketConnected(false);
    };

    setIsSocketConnected(socket.connected);

    socket.on("connect", connect);

    socket.on("disconnect", disconnect);

    return () => {
      socket.off("connect", connect);

      socket.off("disconnect", disconnect);
    };
  }, [socket]);

  // ===============================
  // CHAT HISTORY
  // ===============================

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);

        const res = await getChatHistory({
          userId,
          userType,
        });

        console.log("History:", res);

        setMessages(res?.data?.messages || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [userId, userType]);

  // ===============================
  // JOIN USER CHAT ROOM
  // ===============================

  useEffect(() => {
    if (!socket) return;

    if (!isSocketConnected) return;

    // Join the support chat channel
    socket.emit("join_chat", id);

    // Join the active room
    socket.emit("join_room", {
      chatId: chatId,
      userId: id,
    });

    socket.emit("mark_seen", {
      chatId,
      oppId: userId,
    });

    return () => {
      console.log("Leaving Room:", chatId);

      socket.emit("leave_room", {
        chatId,
      });
    };
  }, [socket, isSocketConnected, chatId]);

  // ===============================
  // SOCKET EVENTS
  // ===============================

  // useEffect(() => {
  //   if (!socket) return;

  //   if (!isSocketConnected) return;

  //   const receiveMessage = (message) => {
  //     console.log(message);
  //     const senderId = message.userId || message.user?._id || message.senderId;
  //     if (senderId === "agent") {
  //       console.log("Ignoring own message (preventing duplication)");
  //       return;
  //     }
  //     setMessages((prev) => [...prev, message]);
  //   };

  //   const newChat = (data) => {
  //     console.log("🔥 NEW CHAT", data);
  //   };

  //   const messageDeleted = ({ messageId }) => {
  //     setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  //   };

  //   const userTyping = ({ userId: sender }) => {
  //     if (sender !== "admin") {
  //       setTyping(true);
  //     }
  //   };

  //   const userStopped = ({ userId: sender }) => {
  //     if (sender !== "admin") {
  //       setTyping(false);
  //     }
  //   };
  //   console.log("Register send_message listener");
  //   socket.on("send_message", receiveMessage);
  //   socket.on("new_chat", newChat);
  //   socket.on("message_deleted", messageDeleted);
  //   socket.on("user_typing", userTyping);
  //   socket.on("user_stopped", userStopped);
  //   socket.on("messages_seen", (data) => {
  //     console.log("Seen:", data);
  //   });

  //   return () => {
  //     socket.off("send_message", receiveMessage);

  //     socket.off("new_chat", newChat);

  //     socket.off("message_deleted", messageDeleted);

  //     socket.off("user_typing", userTyping);

  //     socket.off("user_stopped", userStopped);
  //   };
  // }, [socket, isSocketConnected]);

  useEffect(() => {
    if (!socket || !isSocketConnected) return;

    const receiveMessage = (message) => {
      console.log("📩 Received:", message);

      // Ignore messages from other chat rooms
      if (message.chatId && message.chatId !== chatId) {
        return;
      }

      // Ignore my own echoed message (if backend echoes it)
      if (message.user?._id === id) {
        return;
      }

      setMessages((prev) => {
        // Prevent duplicate messages
        const exists = prev.some((msg) => msg.id === message.id);

        if (exists) {
          console.log("Duplicate message ignored:", message.id);
          return prev;
        }

        return [...prev, message];
      });
    };

    const newChat = (data) => {
      console.log("🔥 NEW CHAT", data);
    };

    const messageDeleted = ({ messageId }) => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    };

    const userTyping = ({ userId }) => {
      console.log("typing", userId);
      if (userId !== id) {
        setTyping(true);
      }
    };

    const userStopped = ({ userId }) => {
      if (userId !== id) {
        setTyping(false);
      }
    };

    const messagesSeen = (data) => {
      // console.log("Seen:", data);
    };

    // Remove old listeners first
    socket.off("send_message", receiveMessage);
    socket.off("new_chat", newChat);
    socket.off("message_deleted", messageDeleted);
    socket.off("user_typing", userTyping);
    socket.off("user_stopped", userStopped);
    socket.off("messages_seen", messagesSeen);

    // Register listeners
    socket.on("send_message", receiveMessage);
    socket.on("new_chat", newChat);
    socket.on("message_deleted", messageDeleted);
    socket.on("user_typing", userTyping);
    socket.on("user_stopped", userStopped);
    socket.on("messages_seen", messagesSeen);

    return () => {
      socket.off("send_message", receiveMessage);
      socket.off("new_chat", newChat);
      socket.off("message_deleted", messageDeleted);
      socket.off("user_typing", userTyping);
      socket.off("user_stopped", userStopped);
      socket.off("messages_seen", messagesSeen);
    };
  }, [socket, isSocketConnected, chatId, id]);
  // ===============================
  // AUTO SCROLL
  // ===============================

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages]);

  // ===============================
  // SEND MESSAGE
  // ===============================

  const sendMessage = () => {
    if (!input.trim()) return;

    if (!socket || !isSocketConnected) {
      alert("Socket not connected");

      return;
    }

    socket.emit("send_message", {
      chatId,
      userId: id,
      roomData: {
        content: input,
        content_type: "Text",
        content_thumbnil: "",
        parent_id: null,
      },
      user: {
        _id: id,
        role: "agent",
      },
    });

    setInput("");
  };

  // ===============================
  // TYPING
  // ===============================

  const handleTyping = (e) => {
    const value = e.target.value;

    setInput(value);

    if (!socket || !isSocketConnected) return;

    socket.emit("is_typing", {
      roomId: chatId,
      userId: id,
    });

    clearTimeout(typingTimeout.current);

    typingTimeout.current = setTimeout(() => {
      socket.emit("stop_typing", {
        roomId: chatId,
        userId: id,
      });
    }, 2000);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="chat-component">
      <div className="chat-header">
        {title}

        <span>{isSocketConnected ? " 🟢 Online" : " 🔴 Offline"}</span>
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="no-messages">No messages yet</div>
        ) : (
          messages.map((msg, index) => {
            const content = msg.message || msg.content || "";

            const sender = msg.sender || msg.senderId || "user";
            console.log(msg);
            return (
              <div
                key={msg.id || index}
                className={`message ${sender === "agent" ? "agent" : "user"}`}
              >
                <div className="message-bubble">
                  <div className="message-text">{content}</div>
                </div>
                <div className="message-header">
                  <span className="time">
                    {new Date(msg.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            );
          })
        )}

        {typing && <div className="typing-indicator">User typing...</div>}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <input
          value={input}
          onChange={handleTyping}
          onKeyDown={(e) => {
            if (e.key === "Enter") sendMessage();
          }}
          placeholder="Type message..."
        />

        <button className="btn-primary" onClick={sendMessage}>
          <Send />
        </button>
      </div>
    </div>
  );
};

export default ChatComponent;
